import { useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import type { CarouselBatch } from "../state/formReducer"
import { useObjectUrlSrc } from "./useObjectUrlSrc"

/**
 * Carousel batch editor. Parity: `creatives.js:376-553`.
 *
 *   - Each batch has an image list (min 2, enforced at submit) + a
 *     required music file.
 *   - Images support per-item reorder via ← / → buttons and per-item
 *     delete. (Drag-drop is intentionally NOT ported — the parity file
 *     flags it as nice-to-have and adding dnd-kit for one widget is
 *     out of scope for T3.4.)
 *   - Duplicate button clones the batch into a new one (images array
 *     sliced, music file reference shared — same as legacy).
 *   - Expand/collapse toggle on the header.
 *   - "Preview" opens a modal to inspect images + play music.
 */

interface Props {
  batches: CarouselBatch[]
  onAddBatch: () => void
  onRemoveBatch: (id: string) => void
  onDuplicateBatch: (id: string) => void
  onUpdateImages: (id: string, images: File[]) => void
  onUpdateMusic: (id: string, music: File | null) => void
  onReorderImages: (id: string, from: number, to: number) => void
  onPreview: (batch: CarouselBatch) => void
}

function ImageThumb({
  file,
  index,
  total,
  onMove,
  onRemove,
}: {
  file: File
  index: number
  total: number
  onMove: (to: number) => void
  onRemove: () => void
}) {
  const srcRef = useObjectUrlSrc(file)
  const [aspectLabel, setAspectLabel] = useState<string>("")
  return (
    <div className="relative overflow-hidden rounded-md border border-[#3a3a3a] bg-black">
      <div className="h-[220px] w-full bg-black">
        <img
          ref={srcRef}
          alt={file.name}
          className="block h-full w-full object-contain"
          onLoad={(e) => {
            const w = e.currentTarget.naturalWidth || 0
            const h = e.currentTarget.naturalHeight || 1
            if (!w || !h) return
            setAspectLabel(prettyAspect(w, h))
          }}
        />
      </div>
      <span className="absolute left-1 top-1 rounded bg-black/70 px-1 text-[10px]">
        #{index + 1}
      </span>
      {aspectLabel ? (
        <span className="absolute left-9 top-1 rounded bg-[#7a8aef]/85 px-1 text-[10px] font-semibold text-white">
          {aspectLabel}
        </span>
      ) : null}
      <div className="absolute bottom-0 right-0 flex gap-0.5 bg-black/70 p-0.5">
        <button
          type="button"
          disabled={index === 0}
          className="px-1 text-xs disabled:opacity-40"
          onClick={() => onMove(index - 1)}
          aria-label="Move left"
        >
          ←
        </button>
        <button
          type="button"
          disabled={index === total - 1}
          className="px-1 text-xs disabled:opacity-40"
          onClick={() => onMove(index + 1)}
          aria-label="Move right"
        >
          →
        </button>
        <button
          type="button"
          className="px-1 text-xs text-red-400"
          onClick={onRemove}
          aria-label="Remove"
        >
          🗑
        </button>
      </div>
    </div>
  )
}

/** Detect well-known aspect buckets; fall back to exact "W:H". */
function prettyAspect(w: number, h: number): string {
  if (!w || !h) return ""
  const ratio = w / h
  const buckets: { label: string; r: number }[] = [
    { label: "9:16", r: 9 / 16 },
    { label: "1:1", r: 1 },
    { label: "4:5", r: 4 / 5 },
    { label: "16:9", r: 16 / 9 },
    { label: "4:3", r: 4 / 3 },
  ]
  for (const b of buckets) {
    if (Math.abs(ratio - b.r) / b.r < 0.03) return b.label
  }
  const g = gcd(w, h)
  return `${Math.round(w / g)}:${Math.round(h / g)}`
}

function gcd(a: number, b: number): number {
  return b ? gcd(b, a % b) : a
}



function BatchCard({
  batch,
  onRemoveBatch,
  onDuplicateBatch,
  onUpdateImages,
  onUpdateMusic,
  onReorderImages,
  onPreview,
}: {
  batch: CarouselBatch
  onRemoveBatch: (id: string) => void
  onDuplicateBatch: (id: string) => void
  onUpdateImages: (id: string, images: File[]) => void
  onUpdateMusic: (id: string, music: File | null) => void
  onReorderImages: (id: string, from: number, to: number) => void
  onPreview: (batch: CarouselBatch) => void
}) {
  const [expanded, setExpanded] = useState(true)
  const imagesInput = useRef<HTMLInputElement | null>(null)
  const musicInput = useRef<HTMLInputElement | null>(null)

  return (
    <div className="rounded-md border border-brand-border bg-brand-bg/40 p-3">
      <div className="flex items-center justify-between">
        <button
          type="button"
          className="flex items-center gap-2 text-left"
          onClick={() => setExpanded((v) => !v)}
        >
          <span className="text-xs">{expanded ? "▲" : "▼"}</span>
          <h5 className="font-semibold">Carousel {batch.index}</h5>
        </button>
        <div className="flex gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onPreview(batch)}
          >
            Preview
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onDuplicateBatch(batch.id)}
            title="Clone"
          >
            📄
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={() => onRemoveBatch(batch.id)}
            title="Delete"
          >
            🗑
          </Button>
        </div>
      </div>
      {expanded ? (
        <div className="mt-3 space-y-3">
          <div>
            <input
              ref={imagesInput}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                const list = Array.from(e.target.files ?? [])
                if (list.length > 0) {
                  onUpdateImages(batch.id, [...batch.images, ...list])
                }
                e.target.value = ""
              }}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => imagesInput.current?.click()}
                className="border border-[#8fa0ef]/40 bg-[#7488e8] text-white hover:bg-[#6b7edc]"
              >
                Add images
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={batch.images.length === 0}
                onClick={() => onUpdateImages(batch.id, [])}
              >
                Clear images
              </Button>
            </div>
            <p className="mt-1 text-xs text-white">
              {batch.images.length >= 1
                ? `${batch.images.length} image(s) selected`
                : "Minimum 1 image required"}
            </p>
            {batch.images.length > 0 ? (
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {batch.images.map((file, i) => (
                  <ImageThumb
                    key={`${file.name}-${i}-${file.size}`}
                    file={file}
                    index={i}
                    total={batch.images.length}
                    onMove={(to) => onReorderImages(batch.id, i, to)}
                    onRemove={() => {
                      const next = batch.images.slice()
                      next.splice(i, 1)
                      onUpdateImages(batch.id, next)
                    }}
                  />
                ))}
              </div>
            ) : null}
          </div>
          <div>
            <input
              ref={musicInput}
              type="file"
              accept=".mp3,audio/mpeg"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null
                if (f) {
                  const name = f.name.toLowerCase()
                  const okType =
                    f.type === "audio/mpeg" ||
                    f.type === "audio/mp3" ||
                    f.type === "" ||
                    name.endsWith(".mp3")
                  if (!okType || !name.endsWith(".mp3")) {
                    window.alert(
                      "Carousel music must be a real .mp3 file (MPEG MP3). Export from your editor as MP3, not M4A/WAV renamed.",
                    )
                    e.target.value = ""
                    return
                  }
                }
                onUpdateMusic(batch.id, f)
                e.target.value = ""
              }}
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => musicInput.current?.click()}
              className="border border-[#8fa0ef]/40 bg-[#7488e8] text-white hover:bg-[#6b7edc]"
            >
              Add music
            </Button>
            <p className="mt-1 text-xs text-white">
              {batch.music
                ? `Music: ${batch.music.name}`
                : "MP3 required (≥2s, under 10 MB)"}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export function CarouselBatches({
  batches,
  onAddBatch,
  onRemoveBatch,
  onDuplicateBatch,
  onUpdateImages,
  onUpdateMusic,
  onReorderImages,
  onPreview,
}: Props) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="text-xs text-white">
          Each carousel batch becomes one carousel ad. Minimum 1 image per
          carousel (TikTok Photo / single-slide carousel).
        </p>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onAddBatch}
          className="border border-[#8fa0ef]/40 bg-[#7488e8] text-white hover:bg-[#6b7edc]"
        >
          ➕ Add Carousel Batch
        </Button>
      </div>
      <div className="mt-3 space-y-3">
        {batches.map((b) => (
          <BatchCard
            key={b.id}
            batch={b}
            onRemoveBatch={onRemoveBatch}
            onDuplicateBatch={onDuplicateBatch}
            onUpdateImages={onUpdateImages}
            onUpdateMusic={onUpdateMusic}
            onReorderImages={onReorderImages}
            onPreview={onPreview}
          />
        ))}
      </div>
    </div>
  )
}
